import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createSystemApi } from "../api";
import {
  ConfirmActionDialog,
} from "../components/SystemDialogs";
import {
  accessStatusTone,
  ACTION_IMPACT,
  allowedAccessActions,
  allowedConfigActions,
  allowedFlagActions,
  allowedMaintActions,
  configStatusTone,
  flagStatusTone,
  formatLabel,
  maintenanceStatusTone,
  summarizeOpaqueObject,
} from "../labels";
import type {
  AccessAssignmentDto,
  AccessScopeDto,
  AuditLogDto,
  ConfigurationAction,
  ConfigurationDto,
  EmployeeAccessAction,
  FeatureFlagAction,
  FeatureFlagDto,
  IntegrationEventDto,
  MaintenanceDto,
  MaintenanceModeAction,
  PermissionGrantDto,
} from "../types";

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Stack gap={4}>
      <Text role="small" color="secondary">
        {label}
      </Text>
      <Text role="body">{value}</Text>
    </Stack>
  );
}

export function MaintenanceDetailPage() {
  const { maintenanceId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);
  const toast = useToast();

  const [row, setRow] = useState<MaintenanceDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "System", to: "/app/system" },
    { label: "Maintenance", to: "/app/system/maintenance" },
    { label: row?.id ?? "Detail" },
  ]);

  const load = useCallback(async () => {
    if (!maintenanceId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getMaintenance(maintenanceId);
      setRow(result.maintenance_mode);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, maintenanceId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading maintenance…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Maintenance unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const actions = allowedMaintActions(row.status);
  const dangerous = actions.some((a) => a === "activate" || a === "end");

  return (
    <Stack gap={24}>
      <SectionHeader
        title={`Maintenance ${row.id}`}
        description="Live operational status for a scoped maintenance window."
        actions={
          <Flex gap={8} wrap>
            {actions.length > 0 ? (
              <Button
                variant={dangerous ? "danger" : "secondary"}
                onClick={() => {
                  setActionError(null);
                  setActionOpen(true);
                }}
              >
                Lifecycle actions
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => navigate("/app/system/maintenance")}
            >
              Back
            </Button>
          </Flex>
        }
      />
      <Alert tone="info">
        This is live status, not static configuration. Activate restricts the
        recorded scope; End restores normal operation.
      </Alert>
      <Card>
        <Flex gap={24} wrap>
          <StatusChip tone={maintenanceStatusTone(row.status)}>
            {formatLabel(row.status)}
          </StatusChip>
          <Field label="Scope type" value={formatLabel(row.scope_type)} />
          <Field label="Scope ID" value={row.scope_id ?? "—"} />
          <Field label="Starts at" value={row.starts_at} />
          <Field label="Ends at" value={row.ends_at ?? "—"} />
          <Field label="Owner user" value={row.owner_user_id ?? "—"} />
          <Field
            label="Incident reference"
            value={row.incident_reference ?? "—"}
          />
          <Field label="Approval ID" value={row.approval_id ?? "—"} />
        </Flex>
      </Card>
      <InvestigationLinks
        targetType="maintenance_mode"
        targetId={row.id}
      />
      <ConfirmActionDialog
        open={actionOpen}
        title="Confirm maintenance action"
        notice={`Impact depends on the action. ${Object.entries(ACTION_IMPACT)
          .filter(([k]) => actions.includes(k as MaintenanceModeAction))
          .map(([, v]) => v)
          .join(" ")}`}
        actions={actions}
        askApprovalId
        confirmVariant={dangerous ? "danger" : "primary"}
        busy={busy}
        error={actionError}
        onClose={() => setActionOpen(false)}
        onSubmit={async ({ action, reason, approval_id }) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyMaintenanceAction(
              row.id,
              action as MaintenanceModeAction,
              {
                ...(reason ? { reason } : {}),
                ...(approval_id ? { approval_id } : {}),
              },
            );
            toast.push({
              title: `Maintenance ${formatLabel(action)}`,
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

export function ConfigurationDetailPage() {
  const { configurationId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);
  const toast = useToast();

  const [row, setRow] = useState<ConfigurationDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "System", to: "/app/system" },
    { label: "Configurations", to: "/app/system/configurations" },
    { label: row?.id ?? "Detail" },
  ]);

  const load = useCallback(async () => {
    if (!configurationId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getConfiguration(configurationId);
      setRow(result.configuration);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, configurationId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading configuration…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Configuration unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const actions = allowedConfigActions(row.status);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={`Configuration ${row.id}`}
        description="Static governed configuration — values withheld."
        actions={
          <Flex gap={8} wrap>
            {actions.length > 0 ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setActionError(null);
                  setActionOpen(true);
                }}
              >
                Lifecycle actions
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => navigate("/app/system/configurations")}
            >
              Back
            </Button>
          </Flex>
        }
      />
      <Alert tone="warning">
        Configuration value contents are never displayed. There is no editor or
        env dump on this screen.
      </Alert>
      <Card>
        <Flex gap={24} wrap>
          <StatusChip tone={configStatusTone(row.status)}>
            {formatLabel(row.status)}
          </StatusChip>
          <Field
            label="Type"
            value={formatLabel(row.configuration_type)}
          />
          <Field label="Scope type" value={formatLabel(row.scope_type)} />
          <Field label="Scope ID" value={row.scope_id ?? "—"} />
          <Field label="Effective from" value={row.effective_from} />
          <Field label="Effective to" value={row.effective_to ?? "—"} />
          <Field label="Approval ID" value={row.approval_id ?? "—"} />
          <Field
            label="Configuration value"
            value={summarizeOpaqueObject(
              row.configuration_value,
              "configuration_value",
            )}
          />
        </Flex>
      </Card>
      <InvestigationLinks
        targetType="platform_configuration"
        targetId={row.id}
      />
      <ConfirmActionDialog
        open={actionOpen}
        title="Confirm configuration action"
        notice="Lifecycle only — does not edit configuration values. Activate/pause/retire change whether this record is live for its scope."
        actions={actions}
        askApprovalId
        busy={busy}
        error={actionError}
        onClose={() => setActionOpen(false)}
        onSubmit={async ({ action, reason, approval_id }) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyConfigurationAction(
              row.id,
              action as ConfigurationAction,
              {
                ...(reason ? { reason } : {}),
                ...(approval_id ? { approval_id } : {}),
              },
            );
            toast.push({
              title: `Configuration ${formatLabel(action)}`,
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

export function FeatureFlagDetailPage() {
  const { flagId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);
  const toast = useToast();

  const [row, setRow] = useState<FeatureFlagDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "System", to: "/app/system" },
    { label: "Feature flags", to: "/app/system/feature-flags" },
    { label: row?.flag_key ?? "Detail" },
  ]);

  const load = useCallback(async () => {
    if (!flagId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getFeatureFlag(flagId);
      setRow(result.feature_flag);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, flagId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading feature flag…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Feature flag unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const actions = allowedFlagActions(row.status);
  const dangerous = actions.some(
    (a) => a === "enable" || a === "disable" || a === "rollback",
  );

  return (
    <Stack gap={24}>
      <SectionHeader
        title={row.flag_key}
        description={`Feature flag ${row.id}`}
        actions={
          <Flex gap={8} wrap>
            {actions.length > 0 ? (
              <Button
                variant={dangerous ? "danger" : "secondary"}
                onClick={() => {
                  setActionError(null);
                  setActionOpen(true);
                }}
              >
                Lifecycle actions
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => navigate("/app/system/feature-flags")}
            >
              Back
            </Button>
          </Flex>
        }
      />
      <Alert tone="info">
        Viewing an existing flag record. Scope definition values are withheld.
        Enable/disable/rollback change availability for the rollout scope.
      </Alert>
      <Card>
        <Flex gap={24} wrap>
          <StatusChip tone={flagStatusTone(row.status)}>
            {formatLabel(row.status)}
          </StatusChip>
          <Field label="Rollout type" value={formatLabel(row.rollout_type)} />
          <Field label="Scheduled at" value={row.scheduled_at ?? "—"} />
          <Field label="Enabled at" value={row.enabled_at ?? "—"} />
          <Field label="Disabled at" value={row.disabled_at ?? "—"} />
          <Field
            label="Rollback reference"
            value={row.rollback_reference ?? "—"}
          />
          <Field label="Approval ID" value={row.approval_id ?? "—"} />
          <Field
            label="Scope definition"
            value={summarizeOpaqueObject(
              row.scope_definition,
              "scope_definition",
            )}
          />
        </Flex>
      </Card>
      <InvestigationLinks targetType="feature_flag" targetId={row.id} />
      <ConfirmActionDialog
        open={actionOpen}
        title="Confirm feature flag action"
        notice="Protected enable/disable/rollback actions change feature availability immediately for the rollout scope."
        actions={actions}
        askApprovalId
        askScheduledAt
        confirmVariant={dangerous ? "danger" : "primary"}
        busy={busy}
        error={actionError}
        onClose={() => setActionOpen(false)}
        onSubmit={async ({ action, reason, approval_id, scheduled_at }) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyFeatureFlagAction(
              row.id,
              action as FeatureFlagAction,
              {
                ...(reason ? { reason } : {}),
                ...(approval_id ? { approval_id } : {}),
                ...(action === "schedule" && scheduled_at !== undefined
                  ? { scheduled_at }
                  : {}),
              },
            );
            toast.push({
              title: `Feature flag ${formatLabel(action)}`,
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

export function AccessDetailPage() {
  const { assignmentId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);
  const toast = useToast();

  const [row, setRow] = useState<AccessAssignmentDto | null>(null);
  const [scopes, setScopes] = useState<AccessScopeDto[]>([]);
  const [grants, setGrants] = useState<PermissionGrantDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "System", to: "/app/system" },
    { label: "Access", to: "/app/system/access" },
    { label: row?.id ?? "Detail" },
  ]);

  const load = useCallback(async () => {
    if (!assignmentId) return;
    setLoading(true);
    setError(null);
    try {
      const [assignment, scopeResult, grantResult] = await Promise.all([
        api.getAccessAssignment(assignmentId),
        api.listAccessScopes(assignmentId),
        api.listPermissionGrants(assignmentId),
      ]);
      setRow(assignment.assignment);
      setScopes(scopeResult.scopes);
      setGrants(grantResult.grants);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
      setScopes([]);
      setGrants([]);
    } finally {
      setLoading(false);
    }
  }, [api, assignmentId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading access assignment…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Access assignment unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const actions = allowedAccessActions(row.status);
  const dangerous = actions.some(
    (a) => a === "suspend" || a === "revoke" || a === "expire",
  );

  return (
    <Stack gap={24}>
      <SectionHeader
        title={`Access ${row.id}`}
        description="Employee access assignment — no credentials or tokens."
        actions={
          <Flex gap={8} wrap>
            {actions.length > 0 ? (
              <Button
                variant={dangerous ? "danger" : "secondary"}
                onClick={() => {
                  setActionError(null);
                  setActionOpen(true);
                }}
              >
                Lifecycle actions
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => navigate("/app/system/access")}
            >
              Back
            </Button>
          </Flex>
        }
      />
      <Card>
        <Flex gap={24} wrap>
          <StatusChip tone={accessStatusTone(row.status)}>
            {formatLabel(row.status)}
          </StatusChip>
          <Field label="User ID" value={row.user_id} />
          <Field label="Role" value={formatLabel(row.employee_role)} />
          <Field label="Effective from" value={row.effective_from} />
          <Field label="Effective to" value={row.effective_to ?? "—"} />
          <Field
            label="Approved by"
            value={row.approved_by_user_id ?? "—"}
          />
          <Field label="Reason" value={row.reason ?? "—"} />
        </Flex>
      </Card>

      <SectionHeader title="Scopes" description="Access scope records." />
      {scopes.length === 0 ? (
        <Text role="small" color="secondary">
          No scopes on file.
        </Text>
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>Scope type</Th>
              <Th>Entity</Th>
              <Th>Level</Th>
              <Th>Effective</Th>
            </Tr>
          </Thead>
          <Tbody>
            {scopes.map((scope) => (
              <Tr key={scope.id}>
                <Td>{formatLabel(scope.scope_type)}</Td>
                <Td>{scope.scope_entity_id ?? "—"}</Td>
                <Td>{formatLabel(scope.permission_level)}</Td>
                <Td>
                  {scope.effective_from}
                  {scope.effective_to ? ` → ${scope.effective_to}` : ""}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <SectionHeader
        title="Permission grants"
        description="Module/action grants. Condition sets summarized by count only."
      />
      {grants.length === 0 ? (
        <Text role="small" color="secondary">
          No grants on file.
        </Text>
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>Module</Th>
              <Th>Action</Th>
              <Th>Status</Th>
              <Th>Conditions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {grants.map((grant) => (
              <Tr key={grant.id}>
                <Td>{grant.module}</Td>
                <Td>{grant.action}</Td>
                <Td>{formatLabel(grant.status)}</Td>
                <Td>
                  {Array.isArray(grant.condition_set)
                    ? `${grant.condition_set.length} condition(s) — values withheld`
                    : "—"}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <InvestigationLinks
        targetType="employee_access_assignment"
        targetId={row.id}
      />

      <ConfirmActionDialog
        open={actionOpen}
        title="Confirm access action"
        notice="Suspend/revoke/expire remove or pause staff capabilities. Approve/activate/renew change assignment lifecycle only."
        actions={actions}
        confirmVariant={dangerous ? "danger" : "primary"}
        busy={busy}
        error={actionError}
        onClose={() => setActionOpen(false)}
        onSubmit={async ({ action, reason }) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyAccessAssignmentAction(
              row.id,
              action as EmployeeAccessAction,
              {
                ...(reason ? { reason } : {}),
              },
            );
            toast.push({
              title: `Access ${formatLabel(action)}`,
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

export function AuditLogDetailPage() {
  const { auditLogId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);

  const [row, setRow] = useState<AuditLogDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "System", to: "/app/system" },
    { label: "Audit logs", to: "/app/system/audit-logs" },
    { label: row?.id ?? "Detail" },
  ]);

  const load = useCallback(async () => {
    if (!auditLogId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getAuditLog(auditLogId);
      setRow(result.audit_log);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, auditLogId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading audit log…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Audit log unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title={`Audit ${row.id}`}
        description="Read-only investigation record."
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate("/app/system/audit-logs")}
          >
            Back
          </Button>
        }
      />
      <Alert tone="info">
        Prior state, resulting state, and security context values are withheld
        to avoid exposing secrets or personal data.
      </Alert>
      <Card>
        <Flex gap={24} wrap>
          <Field label="Occurred at" value={row.occurred_at} />
          <Field label="Action" value={row.action} />
          <Field
            label="Target"
            value={`${row.target_type} · ${row.target_id}`}
          />
          <Field label="Actor" value={row.actor_user_id ?? "—"} />
          <Field
            label="Source workflow"
            value={row.source_workflow_id ?? "—"}
          />
          <Field label="Reason" value={row.reason ?? "—"} />
          <Field label="Approval ID" value={row.approval_id ?? "—"} />
          <Field
            label="Classification"
            value={formatLabel(row.classification)}
          />
          <Field
            label="Prior state"
            value={summarizeOpaqueObject(row.prior_state, "prior_state")}
          />
          <Field
            label="Resulting state"
            value={summarizeOpaqueObject(
              row.resulting_state,
              "resulting_state",
            )}
          />
          <Field
            label="Security context"
            value={summarizeOpaqueObject(
              row.security_context,
              "security_context",
            )}
          />
        </Flex>
      </Card>
    </Stack>
  );
}

export function IntegrationEventDetailPage() {
  const { eventId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);

  const [row, setRow] = useState<IntegrationEventDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "System", to: "/app/system" },
    {
      label: "Integration events",
      to: "/app/system/integration-events",
    },
    { label: row?.id ?? "Detail" },
  ]);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getIntegrationEvent(eventId);
      setRow(result.integration_event);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading integration event…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Integration event unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title={`Integration event ${row.id}`}
        description="Operational event — payload contents not expanded."
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate("/app/system/integration-events")}
          >
            Back
          </Button>
        }
      />
      <Card>
        <Flex gap={24} wrap>
          <Field label="Status" value={row.status} />
          <Field label="Direction" value={row.direction} />
          <Field label="Integration type" value={row.integration_type} />
          <Field label="Occurred at" value={row.occurred_at} />
          <Field label="Completed at" value={row.completed_at ?? "—"} />
          <Field label="Attempt count" value={String(row.attempt_count)} />
          <Field label="Correlation ID" value={row.correlation_id ?? "—"} />
          <Field
            label="Verified external reference"
            value={row.verified_external_reference ?? "—"}
          />
          <Field
            label="Payload reference"
            value={
              row.payload_reference
                ? "Reference on file (contents not expanded)"
                : "—"
            }
          />
          <Field
            label="Domain event"
            value={row.domain_event_id ?? "—"}
          />
          <Field
            label="Resulting domain event"
            value={row.resulting_domain_event_id ?? "—"}
          />
          <Field
            label="Ingress record"
            value={row.integration_ingress_record_id ?? "—"}
          />
          <Field label="Failure reason" value={row.failure_reason ?? "—"} />
          <Field
            label="Quarantined at"
            value={row.quarantined_at ?? "—"}
          />
        </Flex>
      </Card>
      <InvestigationLinks
        targetType="integration_event"
        targetId={row.id}
        correlationId={row.correlation_id}
      />
    </Stack>
  );
}

function InvestigationLinks({
  targetType,
  targetId,
  correlationId,
}: {
  targetType: string;
  targetId: string;
  correlationId?: string | null;
}) {
  const navigate = useNavigate();
  const auditTo = `/app/system/audit-logs?target_type=${encodeURIComponent(targetType)}&target_id=${encodeURIComponent(targetId)}`;
  const integrationTo = correlationId
    ? `/app/system/integration-events?correlation_id=${encodeURIComponent(correlationId)}`
    : "/app/system/integration-events";

  return (
    <Card>
      <Stack gap={8}>
        <Text role="title">Investigation</Text>
        <Text role="small" color="secondary" as="p">
          Jump to filtered audit / integration queues using contract identifiers
          only.
        </Text>
        <Flex gap={8} wrap>
          <Button
            variant="secondary"
            onClick={() => navigate(auditTo)}
          >
            Related audit logs
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate(integrationTo)}
          >
            Integration events
          </Button>
        </Flex>
      </Stack>
    </Card>
  );
}
